import { RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from './input.component';
import { Button } from './button.component';
import utils from '../features/utils';
import { HmacSHA1 } from 'crypto-js';
import { API } from '../constants/api.constants';
import { AXIOS, CommonPasswordService, InfoPassword } from '@umanforge/common';
import { AxiosError } from 'axios';


interface ChangePasswordProps {
  title?: string;
  subtitle?: string;
  buttonText: string;
  onClick: ( password: string ) => Promise<boolean>;
  onAxiosError: ( err: AxiosError ) => void;
}

export const NeedChangePassword = ( { title, subtitle, buttonText, onClick, onAxiosError }: ChangePasswordProps ) => {
  const { t } = useTranslation();
  const passwordService = new CommonPasswordService();

  const passwordRef = useRef<HTMLInputElement>( null );
  const passwordBisRef = useRef<HTMLInputElement>( null );

  const [ isBusy, setIsBusy ] = useState<boolean>( false );
  const [ canSendPassword, setCanSendPassword ] = useState<boolean>( false );
  const [ constraintsPassword ] = useState<InfoPassword[]>(
    passwordService.passwordSchema.properties
      .filter( item => { return [ 'min', 'min', 'max', 'uppercase', 'lowercase', 'digits', 'symbols' ].includes( item.method ); } )
      .map( item => { return { validation: item.method, arguments: item.arguments[ 0 ], message: item.arguments[ 1 ] } as InfoPassword; } )
      .concat( [ passwordService.SamePasswordSchema ] )
  );
  const [ resultConstraintsPassword, setResultConstraintsPassword ] = useState<InfoPassword[]>(
    passwordService.validatePassword( String( ( passwordRef as RefObject<HTMLInputElement> ).current?.value ) )
  );

  useEffect( () => {
    onChange();
  }, [] );

  const onChange = () => {
    const password = String( ( passwordRef as RefObject<HTMLInputElement> ).current?.value );
    const passwordBis = String( ( passwordBisRef as RefObject<HTMLInputElement> ).current?.value );
    const resultValidatePassword = passwordService.validatePassword( password );
    if ( password != passwordBis || password === '' ) {
      resultValidatePassword.push( passwordService.SamePasswordSchema );
    }
    setResultConstraintsPassword( resultValidatePassword );
    setCanSendPassword( resultValidatePassword.length === 0 );
  };

  const onSavePassword = ( password: string ) => {
    setIsBusy( true );
    AXIOS.get( API.INFOS_AUTH )
      .then( async response => {
        // eslint-disable-next-line new-cap
        const isChange = await onClick( HmacSHA1( password, String( response.data.token ) ).toString() );
        if ( isChange ) {
          utils.connectUser();
        }
      } )
      .catch( err => {
        onAxiosError( err );
      } )
      .finally( () => {
        setIsBusy( false );
      } );
  };

  return (
    <div>
      <div className='flex flex-col gap-4 mx-4'>
        <div className='flex flex-col gap-4 w-full'>
          <p className='text-xl text-center font-bold'> { title } </p>
          <i className='text-sm'> { subtitle } </i>
        </div>
        <div className="flex flex-col gap-4">
          <Input
            label={ t( 'Password' ) }
            placeholder='ZhP270j9${r%'
            ref={ passwordRef }
            isPassword={ true }
            onChange={ onChange }
          />
          <Input
            label={ t( 'Confirm password' ) }
            placeholder='ZhP270j9${r%'
            ref={ passwordBisRef }
            isPassword={ true }
            onChange={ onChange }
          />
        </div>
        <div className='text-xs text-left mx-1' >
          {
            constraintsPassword.map( constraint => {
              return (
                <div
                  key={ constraint.validation }
                  className='flex flex-cols pt-[2px] items-center gap-1'
                >
                  {
                    (
                      resultConstraintsPassword.find( resultContraint => { return resultContraint.validation === constraint.validation; } ) ?
                        <svg
                          className='w-3 h-3 fill-error'
                          viewBox='0 0 384 512'
                        >
                          <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"/>
                        </svg>
                        :
                        <svg
                          className='w-3 h-3 fill-validate'
                          viewBox='0 0 448 512'
                        >
                          <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
                        </svg>
                    )
                  }
                  <p className={
                    (
                      resultConstraintsPassword.find( resultContraint => { return resultContraint.validation === constraint.validation; } ) ?
                        'text-error'
                        :
                        'text-validate'
                    )
                  }>
                    {
                      t( constraint.message.replaceAll( '{plural}', constraint.arguments > 1 ? 's' : '' ) )
                        .replace( '{0}', String( constraint.arguments ) )
                    }
                  </p>
                </div>
              );
            } )
          }
        </div>
        <div className='flex justify-center w-full'>
          <div className='min-w-[150px] h-9'>
            <Button
              label={ buttonText }
              action={ () => { onSavePassword( String( passwordRef.current?.value ?? '' ) ); } }
              isLoading={ isBusy }
              disable={ !canSendPassword }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
